import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaExclamationCircle, FaDownload  } from 'react-icons/fa';
import { Download } from "lucide-react";
import FloatingLabelInput from '../../ui/FloatingLabelInput';
import FloatingLabelSelect from '../../ui/FloatingLabelSelect';
import { getMinimumApprovedCustomerByFilters, fetchSubClientData, downloadCustomerFinalPdf, getApprovedComponentsListByCheckinID } from '../../../services/client/customerApiService';
import { fetchServiceTypeData } from '../../../services/client/serviceTypeApiService'; 
import { ProcessingOverlay } from '../../common/ProcessingOverlay';
import CustomRichTable from '../../common/CustomRichTable';
const CustomerReport = () => {
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [apiResponse, setApiResponse] = useState(null);
    const [formValues, setFormValues] = useState({
        clientId: '',
        serviceTypeId: '',
        subClientId: '',
        checkinId: ''
    });
    const [subClients, setSubClients] = useState([]);
    const [serviceTypes, setServiceTypes] = useState([]);
    const [errors, setErrors] = useState({});
    const [customerFiles, setCustomerFiles] = useState([]);
    const navigate = useNavigate(); // Initialize useNavigate

    const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [components, setComponents] = useState([]);

  const showComponentsPopup = async (checkinId) => {
    
        setDownloading(true);
        try {
        const data = await getApprovedComponentsListByCheckinID(checkinId);
            if (data.success && data.success.length > 0) {
                setComponents(data.success);
                setIsPopupOpen(true);
            } else {
                setComponents([]);
            }
        } catch (error) {
            console.error("Submission failed:", error);
            setDownloading(false);
            setComponents([]);
        } finally {
            setDownloading(false);
        }
  };

    const inputClasses = (field) => (errors[field] ? 'border-red-500' : 'border-gray-300');

    useEffect(() => {
        const fetchServiceTypeByClient = async () => {
            try {
                const data = await fetchServiceTypeData(localStorage.getItem('clientID'));
                setServiceTypes(data.map(obj => ({ value: obj.serviceTypeId, label: obj.serviceType })));
            } catch (error) {
                console.error("Error fetching service types:", error);
            }
        };
        fetchServiceTypeByClient();
    }, []);

    useEffect(() => {
        const getSubClients = async () => {
            try {
                const subClientData = await fetchSubClientData(localStorage.getItem('clientID'));
                setSubClients(subClientData.map(subClient => ({
                    value: subClient.subClientId,
                    label: subClient.subClientName
                })));
            } catch (error) {
                console.error("Error fetching sub-clients:", error);
            }
        };
        getSubClients();
    }, []);

    const validate = () => {
        const newErrors = {};
       
        return newErrors;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues({ ...formValues, [name]: value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validate();
        if (Object.keys(validationErrors).length === 0) {
            setLoading(true);
            try {
                 // Manually add the clientId here instead of relying on the setFormValues update
            const updatedFormValues = { ...formValues, clientId: localStorage.getItem('clientID') };

            //console.log(updatedFormValues); // You should see the clientId now included here
            const data = await getMinimumApprovedCustomerByFilters(updatedFormValues);
                if (data.success && data.success.length > 0) {
                    setCustomerFiles(data.success);
                    setApiResponse({ success: 'Files fetched successfully!' });
                } else {
                    setCustomerFiles([]);
                    setApiResponse({ error: 'No Data Found' });
                }
            } catch (error) {
                console.error("Submission failed:", error);
                setApiResponse({ error: 'Failed to submit form. Please try again.' });
                setCustomerFiles([]);
            } finally {
                setLoading(false);
            }
        } else {
            setErrors(validationErrors);
        }
    };


    const handleDownloadPdf = async (checkinId) => {
        setDownloading(true); // Show loading spinner
       
            try {
            
           // console.log(checkinId); // You should see the clientId now included here
            const data = await downloadCustomerFinalPdf(checkinId);
            
            } catch (error) {
                console.error("Submission failed:", error);
            
            } finally {
                setDownloading(false);
            }
    };

    const resetFormValues = () => {
        setFormValues({
            serviceTypeId: '',
            subClientId: '',
            checkinId:''
        });
        setErrors({});
    };

    const handleCancel = () => {
        resetFormValues();
        navigate('/customerReport');
    };

    const columns = [

        { Header: 'ServiceType', accessor: 'serviceType' },
        { Header: 'Client', accessor: 'subClientName' },
        
        { Header: 'Checkin ID', accessor: 'checkinId' },
        { Header: 'Applicant', accessor: 'customerName' },
        {
            Header: 'Show',
            Cell: ({ row }) => (
                <button 
                      onClick={() => {
                          showComponentsPopup(row.checkinId);
                      }} className="bg-green-500 text-white px-3 py-1 mr-2 rounded">
                      Components
                    </button>
            ),
        },
        {
            Header: 'Download',
            Cell: ({ row }) => (
                <button
                      disabled={downloading}
                      onClick={() => {
                          handleDownloadPdf(row.checkinId);
                      }}className="bg-green-500 text-white px-3 py-1 mr-2 rounded">
                      <Download size={20} />
                    </button>
            ),
        },
        
    ];

    return (
        <div className="container mx-auto p-6 bg-white rounded-lg border">
            <form onSubmit={handleSubmit} className="space-y-6 w-full">
                <h1>Client Report</h1>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        {loading ? (
                            <p>Loading Service Types...</p>
                        ) : (
                            <FloatingLabelSelect
                                label="Select Service Type"
                                id="serviceTypeId"
                                name="serviceTypeId"
                                value={serviceTypes.find(st => st.value === formValues.serviceTypeId) || null}
                                onChange={(option) => setFormValues({ ...formValues, serviceTypeId: option.value })}
                                className={inputClasses('serviceTypeId')}
                                options={serviceTypes}
                                isSearchable={true}
                            />
                        )}
                    </div>
                    <div>
                        <FloatingLabelSelect
                            label="Select Client"
                            id="subClientId"
                            name="subClientId"
                            value={subClients.find(subClient => subClient.value === formValues.subClientId) || null}
                            onChange={(option) => setFormValues({ ...formValues, subClientId: option.value })}
                            className={inputClasses('subClientId')}
                            options={subClients}
                            isSearchable={true}
                        />
                    </div>
                    <div>
                        <FloatingLabelInput
                            label="Checkin ID"
                            id="checkinId"
                            name="checkinId"
                            value={formValues.checkinId}
                            onChange={handleChange}
                            className={inputClasses('checkinId')}
                        />
                    </div>
                    <div className="flex gap-4">
                    <button
                        type="submit"
                        className={`px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 ${loading ? 'opacity-50' : ''}`}
                        disabled={loading}
                    >
                        {loading ? 'Searching...' : 'Search'}
                    </button>
                    <button
                        type="button"
                        onClick={handleCancel}
                        className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                    >
                        Cancel
                    </button>
                </div>
                </div>

                
            </form>
            <div>
            <p class="py-2">
            {/* <hr /> */}
            </p>
            {customerFiles.length === 0 ? (
        <p></p>
      ) : (
        // <div className="overflow-x-auto">
        //   <table className="min-w-full table-auto">
        //     <thead>
        //       <tr className="bg-gradient-to-r from-indigo-600 to-indigo-500 text-white shadow-md">
        //       <th className="px-6 py-3 text-xs md:text-sm font-semibold">ServiceType</th>
        //         <th className="px-6 py-3 text-xs md:text-sm font-semibold">Client</th>
        //         <th className="px-6 py-3 text-xs md:text-sm font-semibold">Checkin ID</th>
        //         <th className="px-6 py-3 text-xs md:text-sm font-semibold">Applicant</th>
        //         <th className="px-6 py-3 text-xs md:text-sm font-semibold">Show</th>
        //         <th className="px-6 py-3 text-xs md:text-sm font-semibold">Download</th>
                
        //       </tr>
        //     </thead>
        //     <tbody>
        //       {customerFiles.map((task, index) => (
        //         <tr key={task.taskId} className={`transition-all duration-300 ease-in-out 
        //             ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} 
        //             hover:bg-blue-50 hover:shadow-lg`}>
        //           <td className="border px-4 py-2 text-xs md:text-sm">{task.serviceType}</td>
        //           <td className="border px-4 py-2 text-xs md:text-sm">{task.subClientName}</td>
        //           <td className="border px-4 py-2 text-xs md:text-sm">{task.checkinId}</td>
        //           <td className="border px-4 py-2 text-xs md:text-sm">{task.customerName}</td>
        //           <td className="border px-4 py-2 text-xs md:text-sm">
        //           <button 
        //               onClick={() => {
        //                   showComponentsPopup(task.checkinId);
        //               }} className="bg-green-500 text-white px-3 py-2 mr-2 rounded">
        //               Show Components
        //             </button>
        //           </td>
        //           <td className="border px-4 py-2 text-xs md:text-sm">
        //           <button
        //               disabled={downloading}
        //               onClick={() => {
        //                   handleDownloadPdf(task.checkinId);
        //               }}className="bg-green-500 text-white px-3 py-2 mr-2 rounded">
        //               <Download size={20} />
        //             </button>
        //           </td>

                  
                  
        //         </tr>
        //       ))}
        //     </tbody>
        //   </table>
        // </div>
        <CustomRichTable columns={columns} data={customerFiles} />
      )}
            </div> 

            <ComponentPopup
        isOpen={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
        components={components}
      />

             {/* Display the overlay when processing */}
      <ProcessingOverlay isProcessing={downloading} message="Please wait, we're processing your request..." />   
        </div>
    );
};




export default CustomerReport;

const ComponentPopup = ({ isOpen, onClose, components }) => {
    if (!isOpen) return null;
  
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
        <div className="bg-white p-6 rounded-lg w-3/4 shadow-lg">
          <h2 className="text-lg font-bold mb-4">Components Table</h2>
          <table className="w-full border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-200">
                <th className="border p-2">#</th>
                <th className="border p-2">Checkin ID</th>
                <th className="border p-2">Component Name</th>
                <th className="border p-2">Approved By</th>
                <th className="border p-2">Approved Date</th>
              </tr>
            </thead>
            <tbody>
              {components.length > 0 ? (
                components.map((item, index) => (
                  <tr key={index} className="text-center">
                    <td className="border p-2">{index + 1}</td>
                    <td className="border p-2">{item.checkinId}</td>
                    <td className="border p-2">{item.componentName}</td>
                    <td className="border p-2">{item.approvedBy}</td>
                    <td className="border p-2">{item.approvedDate}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="border p-2 text-center">
                    No components found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <button
            onClick={onClose}
            className="mt-4 bg-red-500 text-white px-4 py-2 rounded"
          >
            Close
          </button>
        </div>
      </div>
    );
  };
  